/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * Small helper UI primitives used by the redesigned Agent Detail tabs.
 * Keeping them here avoids polluting the shared `common/` folder while still
 * providing consistent section styling.
 */

import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { ContentCopy as CopyIcon, Check as CheckIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';

interface SectionCardProps {
  title: React.ReactNode;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  dense?: boolean;
}

export const SectionCard: React.FC<SectionCardProps> = ({
  title,
  icon,
  actions,
  description,
  children,
  dense = false,
}) => {
  const theme = useTheme();
  return (
    <Paper
      variant="outlined"
      sx={{
        p: dense ? { xs: 1.5, sm: 2 } : { xs: 2, sm: 2.5 },
        borderRadius: 2,
        bgcolor: 'background.paper',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: description ? 0.5 : 1.5 }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          {icon ? (
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: 'primary.main',
              }}
            >
              {icon}
            </Box>
          ) : null}
          <Typography variant="subtitle1" fontWeight={700} noWrap>
            {title}
          </Typography>
        </Stack>
        {actions ? <Box>{actions}</Box> : null}
      </Stack>
      {description ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2 }}
        >
          {description}
        </Typography>
      ) : null}
      {children}
    </Paper>
  );
};

interface CopyButtonProps {
  value: string;
  label?: string;
  size?: 'small' | 'medium';
  title?: string;
}

export const CopyButton: React.FC<CopyButtonProps> = ({
  value,
  label,
  size = 'small',
  title = 'Copy',
}) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(label ? `${label} copied` : 'Copied to clipboard');
      setTimeout(() => setCopied(false), 1400);
    } catch {
      toast.error('Unable to copy to clipboard');
    }
  };
  return (
    <Tooltip title={copied ? 'Copied!' : title}>
      <IconButton size={size} onClick={handleCopy} aria-label="copy">
        {copied ? <CheckIcon fontSize="small" color="success" /> : <CopyIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
};

interface KeyValueProps {
  label: React.ReactNode;
  value: React.ReactNode;
  copyValue?: string;
  mono?: boolean;
}

export const KeyValue: React.FC<KeyValueProps> = ({ label, value, copyValue, mono }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.65rem' }}
    >
      {label}
    </Typography>
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
      <Typography
        component="div"
        variant="body2"
        sx={{
          fontFamily: mono ? 'monospace' : undefined,
          wordBreak: 'break-word',
          flex: 1,
          minWidth: 0,
        }}
      >
        {value}
      </Typography>
      {copyValue ? <CopyButton value={copyValue} size="small" /> : null}
    </Stack>
  </Box>
);

export default SectionCard;
